'use client';

import { useState, useEffect } from 'react';
import { Users, FileText, Download } from 'lucide-react';
import toast from 'react-hot-toast';

interface User {
  id: number;
  clerkId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  companyName?: string | null;
}

interface AutogestionClientProps {
  user: User;
}

interface PriceList {
  id: number;
  category: string;
  displayName: string;
  description: string | null;
  fileName: string | null;
  filePath: string | null;
  fileSize: number | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Predefined categories that should always be displayed
const priceListCategories = [
  {
    id: 'limpieza_hogar_industria',
    name: 'Productos de limpieza para el hogar y la industria',
    description: 'Lista de precios de productos de limpieza para uso doméstico e industrial'
  },
  {
    id: 'automovil',
    name: 'Productos para el automóvil',
    description: 'Lista de precios de productos para el cuidado y mantenimiento del automóvil'
  },
  {
    id: 'piletas_natacion',
    name: 'Productos para piletas de natación',
    description: 'Lista de precios de productos químicos y accesorios para piletas'
  }
];

export default function AutogestionClient({ user }: AutogestionClientProps) {
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch price lists
  useEffect(() => {
    const fetchPriceLists = async () => {
      try {
        const response = await fetch('/api/price-lists');
        if (!response.ok) {
          throw new Error('Error al cargar las listas de precios');
        }
        const data = await response.json();
        setPriceLists(data);
      } catch (error) {
        console.error('Error fetching price lists:', error);
        toast.error('Error al cargar las listas de precios');
      } finally {
        setLoading(false);
      }
    };

    fetchPriceLists();
  }, []);

  const handleDownloadPDF = (list: PriceList) => {
    if (!list.filePath) {
      toast.error('No hay archivo disponible para descargar');
      return;
    }

    try {
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = list.filePath;
      link.download = list.fileName || `${list.displayName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Descargando lista de precios...');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  // Find the price list for a given category
  const getPriceListForCategory = (categoryId: string) => {
    return priceLists.find(list => list.category === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando listas de precios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Users className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Hola, {user.firstName || user.email}
            </h2>
            <p className="text-gray-600">
              {user.companyName ? `${user.companyName} • ` : ''}Revendedor Autorizado
            </p>
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Listas de Precios</h2>
        <p className="text-gray-600">
          Descarga las listas de precios actualizadas para revendedores en formato PDF.
        </p>
      </div>

      {/* Price List Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {priceListCategories.map((category) => {
          const priceList = getPriceListForCategory(category.id);
          const isAvailable = priceList && priceList.filePath;

          return (
            <div
              key={category.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200 flex flex-col"
            >
              <div className="p-6 flex flex-col flex-1">
                {/* Card Header */}
                <div className="flex items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <FileText className={`h-5 w-5 mr-2 ${isAvailable ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <h4 className="text-lg font-semibold text-gray-900">
                        {category.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      {category.description}
                    </p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="border-t border-gray-200 pt-4 mb-4 flex-1">
                  {isAvailable ? (
                    <div className="space-y-2">
                      {priceList.fileName && (
                        <div className="flex items-center text-xs text-gray-500">
                          <span className="font-medium mr-2">Archivo:</span>
                          <span className="truncate">{priceList.fileName}</span>
                        </div>
                      )}
                      {priceList.fileSize && (
                        <div className="flex items-center text-xs text-gray-500">
                          <span className="font-medium mr-2">Tamaño:</span>
                          <span>{(priceList.fileSize / 1024).toFixed(2)} KB</span>
                        </div>
                      )}
                      <div className="flex items-center text-xs text-gray-500">
                        <span className="font-medium mr-2">Actualizado:</span>
                        <span>
                          {new Date(priceList.updatedAt).toLocaleDateString('es-AR')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-amber-600 font-medium">
                        Lista no disponible
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Esta lista de precios estará disponible próximamente
                      </p>
                    </div>
                  )}
                </div>

                {/* Download Button */}
                <button
                  onClick={() => priceList && handleDownloadPDF(priceList)}
                  disabled={!isAvailable}
                  className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${
                    isAvailable
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isAvailable ? 'Descargar PDF' : 'No disponible'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}